import React, { useEffect, useState } from 'react';
import './AdminDeposit.css';
import { db } from "../firebase";
import { ref, onValue, update, remove, get, set, push } from "firebase/database";
import { Plus, Check, X, History, Coins, Save, Settings2 } from 'lucide-react';
import Swal from 'sweetalert2';

const AdminDeposit = () => {
  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [view, setView] = useState('pending');
  const [showModal, setShowModal] = useState(false);
  const [allPayments, setAllPayments] = useState({});
  
  // State for the 4 specific assets as per your provided image
  const [gatewayForm, setGatewayForm] = useState({
    TRX: { address: '', qrUrl: '' },
    ETH: { address: '', qrUrl: '' },
    BTC: { address: '', qrUrl: '' },
    USDC: { address: '', qrUrl: '' }
  });

  const ASSETS = ['TRX', 'ETH', 'BTC', 'USDC'];

  useEffect(() => {
    // Fetch all gateway data to keep fields persistent
    onValue(ref(db, 'adminSettings/payments'), (snap) => {
      if (snap.exists()) {
        const data = snap.val();
        setAllPayments(data);
        // Sync form with DB data so it stays in fields
        setGatewayForm(prev => ({
          TRX: data.TRX || { address: '', qrUrl: '' },
          ETH: data.ETH || { address: '', qrUrl: '' },
          BTC: data.BTC || { address: '', qrUrl: '' },
          USDC: data.USDC || { address: '', qrUrl: '' }
        }));
      }
    });

    // Pending Requests
    onValue(ref(db, 'depositRequests'), (snap) => {
      const data = snap.val();
      setRequests(data ? Object.keys(data).map(k => ({ id: k, ...data[k] })).reverse() : []);
    });

    // History
    onValue(ref(db, 'depositHistory'), (snap) => {
      const data = snap.val();
      setHistory(data ? Object.keys(data).map(k => ({ id: k, ...data[k] })).reverse() : []);
    });
  }, []);

  const handleInputChange = (coin, field, value) => {
    setGatewayForm(prev => ({
      ...prev,
      [coin]: { ...prev[coin], [field]: value }
    }));
  };

  const handleBulkUpdate = async () => {
    try {
      await set(ref(db, 'adminSettings/payments'), {
        ...gatewayForm,
        lastGlobalUpdate: new Date().toISOString()
      });
      setShowModal(false);
      Swal.fire({
        title: 'Success!',
        text: 'All gateways updated simultaneously.',
        icon: 'success',
        confirmButtonColor: '#f3ba2f'
      });
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    }
  };

  const handleAction = async (req, type) => {
    try {
      // Final Status for History (Success/Rejected)
      const finalStatus = type === 'approve' ? 'Success' : 'Rejected';

      if (type === 'approve') {
        const uRef = ref(db, `users/${req.username}`);
        const uSnap = await get(uRef);
        if (uSnap.exists()) {
          const currentBalance = parseFloat(uSnap.val().balance || 0);
          await update(uRef, { balance: (currentBalance + parseFloat(req.amount)).toFixed(2) });
        }
      }

      // Add to History with proper status for User UI
      await push(ref(db, 'depositHistory'), { 
        ...req, 
        status: finalStatus, 
        processedAt: new Date().toISOString() 
      });

      // Remove from Pending
      await remove(ref(db, `depositRequests/${req.id}`));

      Swal.fire({
        title: type === 'approve' ? 'Approved!' : 'Rejected!',
        text: `Transaction for ${req.username} has been ${type}d.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });

    } catch (e) {
      console.error("Action failed:", e);
      Swal.fire('Error', 'Action failed: ' + e.message, 'error');
    }
  };

  return (
    <div className="admin-compact-container">
      <div className="mini-header">
        <div className="luxury-admin-header">
          <div className="header-title-section">
            <Settings2 size={24} color="#f3ba2f" />
            <h2>Deposit Management</h2>
          </div>
          <button onClick={() => setShowModal(true)} className="manage-gateways-btn">
            <Plus size={18} /> Manage Gateways
          </button>
        </div>
        
        <div className="nav-tabs">
          <button className={view === 'pending' ? 'active' : ''} onClick={() => setView('pending')}>
            Pending Requests ({requests.length})
          </button>
          <button className={view === 'history' ? 'active' : ''} onClick={() => setView('history')}>
            <History size={14}/> View History
          </button>
        </div>
      </div>

      <div className="compact-table-card">
        <table className="slim-table">
          <thead>
            <tr>
              <th>User Details</th>
              <th>Asset Type</th>
              <th>Amount (USDT)</th>
              <th>Request Time</th>
              <th>{view === 'pending' ? 'Action Control' : 'Final Status'}</th>
            </tr>
          </thead>
          <tbody>
            {(view === 'pending' ? requests : history).map((item) => (
              <tr key={item.id} className="slim-row">
                <td className="user-td"><b>{item.username}</b></td>
                <td className="asset-td">
                  <span className={`asset-badge ${item.currency}`}>
                    {item.currency || 'USDT'}
                  </span>
                </td>
                <td className="amt-td">${parseFloat(item.amount).toFixed(2)}</td>
                <td className="time-td">{new Date(item.timestamp || item.processedAt).toLocaleString()}</td>
                <td>
                  {view === 'pending' ? (
                    <div className="slim-actions">
                      <button className="check-btn" title="Approve" onClick={() => handleAction(item, 'approve')}><Check size={18}/></button>
                      <button className="x-btn" title="Reject" onClick={() => handleAction(item, 'reject')}><X size={18}/></button>
                    </div>
                  ) : (
                    <span className={`status-tag ${item.status.toLowerCase()}`}>{item.status.toUpperCase()}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        { (view === 'pending' ? requests : history).length === 0 && <div className="no-data">No records found.</div> }
      </div>

      {showModal && (
        <div className="bulk-modal-overlay">
          <div className="bulk-modal">
            <div className="modal-head">
              <div className="head-content">
                <Coins size={22} color="#f3ba2f" />
                <h4>Network Gateway Settings</h4>
              </div>
              <X size={24} className="close-x" onClick={() => setShowModal(false)} />
            </div>
            
            <div className="modal-scroll-body">
              <p className="modal-sub">Update wallet addresses and QR links for all 4 supported assets.</p>
              
              {ASSETS.map((coin) => (
                <div key={coin} className="coin-update-row">
                  <div className="coin-label-brand">
                    <img src={`https://cryptologos.cc/logos/${coin === 'BTC' ? 'bitcoin-btc' : coin === 'ETH' ? 'ethereum-eth' : coin === 'TRX' ? 'tron-trx' : 'usd-coin-usdc'}-logo.png`} alt={coin} />
                    <span>{coin} Gateway</span>
                  </div>
                  <div className="input-group-grid">
                    <div className="input-box">
                      <label>{coin} Address</label>
                      <input 
                        type="text" 
                        value={gatewayForm[coin].address} 
                        onChange={(e) => handleInputChange(coin, 'address', e.target.value)}
                        placeholder={`Paste ${coin} wallet address`}
                      />
                    </div>
                    <div className="input-box">
                      <label>QR Image URL</label>
                      <input 
                        type="text" 
                        value={gatewayForm[coin].qrUrl} 
                        onChange={(e) => handleInputChange(coin, 'qrUrl', e.target.value)}
                        placeholder="https://imgur.com/your-qr.png"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="modal-foot">
              <button className="cancel-btn" onClick={() => setShowModal(false)}>Discard Changes</button>
              <button className="save-bulk-btn" onClick={handleBulkUpdate}>
                <Save size={18} /> Update All Gateways
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDeposit;